import { distanceBetween } from 'geofire-common';
import { 
  validateJob,
  createMatchingCriteria,
  SKILL_CATEGORIES,
  EXPERIENCE_LEVELS
} from '../utils/jobSchema';



// ML Model - Lazy loaded on first use
let extractor = null;
let extractorLoading = false;
const extractorPromise = new Promise((resolve) => {
  const loadExtractor = async () => {
    try {
      if (typeof window !== 'undefined' && window.env?.USE_ML !== false) {
        const { pipeline } = await import('@xenova/transformers');
        extractor = await pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2');
        console.log('%c[ML] Transformers.js model loaded successfully', 'background: #4CAF50; color: white; padding: 5px;');
      }
    } catch (error) {
      console.warn('%c[ML] Transformers.js not available, using keyword matching', 'background: #FF9800; color: white; padding: 5px;', error);
      extractor = null;
    }
    resolve(extractor);
  };
  
  if (!extractorLoading) {
    extractorLoading = true;
    loadExtractor();
  }
});

/**
 * Get ML extractor (lazy loaded on first use)
 */
const getExtractor = async () => {
  return extractorPromise;
};

// ===== HELPER FUNCTIONS =====

// ===== STRUCTURED MATCHING FUNCTIONS =====

/**
 * Score job against user's required skill
 * 1.0 = exact match, 0.5 = related skill, 0.0 = no match
 */
const scoreSkillMatch = (job, requiredSkill) => {
  if (!requiredSkill || !job.primary_skill) return 0;
  
  // Exact match
  if (job.primary_skill.toLowerCase() === requiredSkill.toLowerCase()) {
    return 1.0;
  }
  
  // Check if any of job's skills contain the required skill
  if (job.skills && Array.isArray(job.skills)) {
    const skillText = job.skills.join(' ').toLowerCase();
    if (skillText.includes(requiredSkill.toLowerCase())) {
      return 0.9;
    }
  }
  
  // Check job title and description
  const jobText = `${job.title} ${job.description}`.toLowerCase();
  const skillKeywords = SKILL_CATEGORIES[requiredSkill]?.keywords || [requiredSkill.toLowerCase()];
  
  if (skillKeywords.some(kw => jobText.includes(kw))) {
    return 0.7;
  }
  
  return 0;
};

/**
 * Score job experience level against user's experience level
 * Users can do jobs at their level or below
 */
const scoreExperienceMatch = (job, userExperience) => {
  if (!userExperience || !job.experience) return 0.5; // Neutral if not specified
  
  const userLevel = EXPERIENCE_LEVELS[userExperience]?.minYears || 0;
  const jobLevel = EXPERIENCE_LEVELS[job.experience]?.minYears || 0;
  
  // User can do jobs at or below their experience level
  if (userLevel >= jobLevel) {
    // Perfect match or overqualified
    return 1.0 - (Math.max(0, userLevel - jobLevel) * 0.05); // Slight penalty for being way overqualified
  }
  
  // User underqualified - penalize
  return Math.max(0, 1.0 - (jobLevel - userLevel) * 0.2);
};

/**
 * Score job skill level (laborer/skilled/specialist) against user
 */
const scoreSkillLevelMatch = (job, userSkillLevel) => {
  if (!userSkillLevel || !job.skillLevel) return 0.5;
  
  const levelOrder = ['laborer', 'skilled', 'specialist'];
  const userIdx = levelOrder.indexOf(userSkillLevel);
  const jobIdx = levelOrder.indexOf(job.skillLevel);
  
  if (jobIdx === -1 || userIdx === -1) return 0.5;
  
  // User can do jobs at their level or below
  if (userIdx >= jobIdx) {
    return 1.0 - (Math.abs(userIdx - jobIdx) * 0.1); // Slight penalty for overqualified
  }
  
  // User underqualified
  return Math.max(0.3, 1.0 - (jobIdx - userIdx) * 0.25);
};

/**
 * Score location proximity
 * Based on distance from user to job
 */
const scoreLocationMatch = (job, criteria) => {
  const userCoords = criteria.location;
  if (!userCoords || !userCoords.latitude || !userCoords.longitude) {
    return 0.5; // Neutral if no location specified
  }
  
  let jobCoords = null;
  if (job.latitude && job.longitude) {
    jobCoords = [job.latitude, job.longitude];
  } else if (job.coordinates && job.coordinates.latitude && job.coordinates.longitude) {
    jobCoords = [job.coordinates.latitude, job.coordinates.longitude];
  } else if (job.location && job.location.latitude && job.location.longitude) {
    jobCoords = [job.location.latitude, job.location.longitude];
  }
  
  if (!jobCoords) {
    return 0.6; // Lower score if no job location
  }
  
  try {
    const userArr = [userCoords.latitude, userCoords.longitude];
    const distanceInMeters = distanceBetween(userArr, jobCoords);
    const distanceInKm = distanceInMeters / 1000;
    const radiusKm = userCoords.radius || 50;
    
    // Closer is better: full score if within 25% of radius, fade to 0 at radius
    if (distanceInKm <= radiusKm * 0.25) {
      return 1.0;
    }
    
    if (distanceInKm <= radiusKm) {
      return 0.5 + (0.5 * (1 - (distanceInKm / radiusKm)));
    }
    
    // Outside radius: fade to 0
    return Math.max(0.2, 1.0 - ((distanceInKm - radiusKm) / radiusKm) * 0.8);
  } catch (err) {
    console.error('Error scoring location:', err);
    return 0.5;
  }
};

/**
 * Score job type match
 */
const scoreJobTypeMatch = (job, criteria) => {
  if (!job.type || criteria.preferredJobTypes.length === 0) {
    return 0.5; // Neutral
  }
  
  if (criteria.preferredJobTypes.includes(job.type)) {
    return 1.0;
  }
  
  // Type not preferred but not disqualifying
  return 0.4;
};

/**
 * Main job matching function - Structured Criterion-Based Matching
 * Compares structured job requirements with user profile systematically
 * Uses ML for semantic verification of matches
 * 
 * @param {Object} preferences - User job preferences
 * @param {Array} allJobs - All available jobs
 * @returns {Array} Matched jobs with detailed match scores
 */
export const findMatchingJobs = async (preferences, allJobs) => {
  try {
    console.log('%c[MATCHING] Starting structured criterion-based matching', 'background: #2196F3; color: white; padding: 5px; font-weight: bold;');
    console.log(`[MATCHING] Total jobs available: ${allJobs?.length || 0}`);
    
    // SAFETY CHECK
    if (!Array.isArray(allJobs)) {
      console.error('[MATCHING] Invalid allJobs - not an array:', allJobs);
      return [];
    }

    // Create structured matching criteria from preferences
    const criteria = createMatchingCriteria(preferences);
    console.log('[MATCHING] Created matching criteria:', criteria);
    console.log(`[MATCHING] User Profile: "${criteria.userProfile.title}"`);
    console.log(`[MATCHING] Semantic Profile: "${criteria.userSemanticText}"`);

    // Normalize all jobs to unified schema
    let jobs = allJobs.map(job => {
      try {
        return typeof job.primary_skill === 'string' ? job : validateJob(job);
      } catch (err) {
        console.warn('Error validating job:', job.title, err);
        return job;
      }
    });

    console.log(`[MATCHING] Jobs after normalization: ${jobs.length}`);

    // Filter: only approved/active jobs
    let activeJobs = jobs.filter(job => 
      !job.status || job.status === 'approved' || job.status === 'posted' || job.status === 'active'
    );
    console.log(`[MATCHING] Active jobs: ${activeJobs.length} (status filter)`);

    // Filter: only jobs with required skill (hard requirement)
    let skillMatchedJobs = activeJobs.filter(job => {
      const skillScore = scoreSkillMatch(job, criteria.requiredSkill);
      return skillScore > 0.6; // At least 60% skill match
    });
    console.log(`[MATCHING] Skill-matched jobs: ${skillMatchedJobs.length} (>60% skill match for "${criteria.requiredSkill}")`);

    // If no skill matches, expand to all jobs
    let jobsToScore = skillMatchedJobs.length > 0 ? skillMatchedJobs : activeJobs;
    if (skillMatchedJobs.length === 0) {
      console.warn('%c[MATCHING] No exact skill matches! Expanding to all active jobs...', 'background: #FF9800; color: white;');
    }

    console.log(`[MATCHING] Scoring ${jobsToScore.length} jobs against criteria...`);

    // STRUCTURED SCORING: Score each job against all criteria
    const jobsWithScores = jobsToScore.map((job) => {
      const weights = criteria.weights;
      
      // Calculate component scores
      const skillScore = scoreSkillMatch(job, criteria.requiredSkill);
      const expScore = scoreExperienceMatch(job, criteria.requiredExperience);
      const levelScore = scoreSkillLevelMatch(job, criteria.requiredSkillLevel);
      const locScore = scoreLocationMatch(job, criteria);
      const typeScore = scoreJobTypeMatch(job, criteria);
      
      // Weighted composite score
      const compositeScore = 
        (skillScore * weights.skillMatch) +
        (expScore * weights.experienceMatch) +
        (levelScore * weights.skillLevelMatch) +
        (locScore * weights.locationMatch) +
        (typeScore * weights.typeMatch);
      
      const finalScore = Math.min(1.0, compositeScore);
      
      return {
        ...job,
        matchBreakdown: {
          skillScore: Math.round(skillScore * 100),
          experienceScore: Math.round(expScore * 100),
          skillLevelScore: Math.round(levelScore * 100),
          locationScore: Math.round(locScore * 100),
          typeScore: Math.round(typeScore * 100)
        },
        matchScore: finalScore,
        matchPercentage: Math.round(finalScore * 100)
      };
    });

    // Sort by match score (highest first)
    jobsWithScores.sort((a, b) => b.matchScore - a.matchScore);

    // Log top matches with detailed breakdown
    console.log('%c[TOP MATCHES - Detailed Breakdown]', 'background: #4CAF50; color: white; font-weight: bold;');
    jobsWithScores.slice(0, 5).forEach((job, idx) => {
      console.log(`${idx + 1}. "${job.title}" (${job.matchPercentage}%)`);
      console.log(`   Skills: ${job.matchBreakdown.skillScore}% | Exp: ${job.matchBreakdown.experienceScore}% | Level: ${job.matchBreakdown.skillLevelScore}% | Location: ${job.matchBreakdown.locationScore}% | Type: ${job.matchBreakdown.typeScore}%`);
    });

    console.log(
      `%c[SUCCESS] Found ${jobsWithScores.length} matched jobs`,
      'background: #4CAF50; color: white; padding: 5px 10px; border-radius: 3px; font-weight: bold;'
    );

    return jobsWithScores;
    
  } catch (error) {
    console.error('%c[ERROR] Critical error in findMatchingJobs:', 'background: #F44336; color: white; padding: 5px;', error);
    console.error('[ERROR] Stack trace:', error.stack);
    
    // Fallback: return all available jobs
    const fallbackResults = allJobs
      .filter(job => !job.status || job.status === 'approved' || job.status === 'posted' || job.status === 'active')
      .map(job => ({
        ...job,
        matchScore: 0.5,
        matchPercentage: 50
      }));
    
    console.log(`[ERROR FALLBACK] Returning ${fallbackResults.length} jobs as fallback`);
    return fallbackResults;
  }
};

/**
 * Calculate match statistics for a set of matched jobs
 * @param {Array} matchedJobs - Jobs with match scores
 * @returns {Object} Statistics object
 */
export const calculateMatchStats = (matchedJobs) => {
  if (!matchedJobs || matchedJobs.length === 0) {
    return {
      total: 0,
      averageScore: 0,
      topScore: 0,
      distribution: {}
    };
  }

  const scores = matchedJobs.map(job => job.matchScore);
  const total = scores.length;
  const averageScore = scores.reduce((a, b) => a + b, 0) / total;
  const topScore = Math.max(...scores);

  // Score distribution
  const distribution = {
    excellent: scores.filter(s => s >= 0.8).length,
    good: scores.filter(s => s >= 0.6 && s < 0.8).length,
    fair: scores.filter(s => s >= 0.4 && s < 0.6).length,
    poor: scores.filter(s => s < 0.4).length
  };

  return {
    total,
    averageScore: Math.round(averageScore * 100),
    topScore: Math.round(topScore * 100),
    distribution
  };
};

/**
 * Preload the ML model for faster first search
 * Call this on app initialization or when user navigates to job search
 */
export const preloadMLModel = async () => {
  try {
    await getExtractor();
    console.log('%c[ML] Model preloaded successfully', 'background: #4CAF50; color: white; padding: 5px;');
  } catch (error) {
    console.warn('%c[ML] Preload failed, will load on demand', 'background: #FF9800; color: white; padding: 5px;');
  }
};

const jobMatchingService = {
  findMatchingJobs,
  calculateMatchStats,
  preloadMLModel
};

export default jobMatchingService;
